/**
 * React entry-point.
 *
 * Drie verantwoordelijkheden: hostname-mode-detectie (superadmin vs
 * tenant — bepaalt welke router-config en welk auth-context-pad we
 * gebruiken), TanStack-Query-client opzetten (eén instance voor de hele
 * app), en de root mounten.
 *
 * De Aurora-tokens + onze app-stijlen worden eerst geïmporteerd zodat
 * iedere component-CSS daarna kan refereren aan `--color-primary`, etc.
 */
import React from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';

import { resolveAppMode } from '@shared/app-mode';
import { AppModeProvider } from '@shared/app-mode-context';
import { initObservability } from '@shared/observability';
import { i18n } from '@shared/i18n';
import { I18nextProvider } from 'react-i18next';
import { installAuthErrorHandler } from '@shared/api/client';
import { useAuthStore } from '@shared/stores/auth';
import { TranslatedAuroraLabels } from '@shared/components/TranslatedAuroraLabels';
import { App } from './App';

import '@aurora/fonts.css';
// Volgorde is betekenisvol (issue #20): de package-bundel levert de
// component-styles + (verouderde rose-)tokens; onze vendored tokens.css
// erná overschrijft de tokens naar v2.5.0 "Lochmara" tot het DS-team een
// nieuwe package-release publiceert. colors_and_type.css is de lokale
// aanvullingslaag.
import '@bcs-bv/design-system/styles.css';
import '@aurora/tokens.css';
import '@aurora/colors_and_type.css';
import '@aurora/package-patches.css';
import './styles/base.css';

const mode = resolveAppMode(window.location.hostname);

// Sentry zo vroeg mogelijk (vóór de React-mount) zodat ook mount-fouten
// gevangen worden. Zonder VITE_SENTRY_DSN is dit een no-op (ADR-0018).
initObservability(mode === 'superadmin' ? 'superadmin' : 'tenant', window.location.hostname);

// Auth-store context koppelen vóór mount zodat het login-scherm direct
// de juiste base-URL (`/api/v1/central/auth` vs `/api/v1/auth`) gebruikt.
useAuthStore.getState().setContext(mode === 'superadmin' ? 'superadmin' : 'tenant');

// Eén Query-client voor de hele app. Default-stale-time = 30s zodat tab-
// switches geen vol refetch-festival worden, maar mutaties wel snel
// reageren via expliciete `invalidateQueries`.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// 401-redirector werkt los van React Router omdat de api-client geen
// router-context heeft. We geven een navigate-callback mee zodra de
// router gemount is — gebeurt in <App/>. De clear-callback wist de lokale
// auth-staat zodat de login-guard geen verouderde user meer ziet.
installAuthErrorHandler('/login', () => useAuthStore.getState().clearSession());

const container = document.getElementById('app');
if (container === null) {
  throw new Error('Mount target #app not found in index.html');
}

createRoot(container).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18n}>
        {/* De a11y-teksten ín de Aurora-componenten, uit i18n (issue 294). */}
        <TranslatedAuroraLabels>
          <AppModeProvider mode={mode}>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </AppModeProvider>
        </TranslatedAuroraLabels>
      </I18nextProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
